import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const ActivitiesSection = styled.section`
  padding: 5rem 2rem;
  background: #ffffff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
`;

const ActivityList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const ActivityItem = styled.div`
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.3rem;
    margin-bottom: 1rem;
    color: #333;
  }

  p {
    color: #666;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const PhotoGallerySection = styled.div`
  text-align: center;
`;

const ToggleButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 30px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #555;
  }
`;

const PhotoGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
  padding: 1rem;
`;

const PhotoCard = styled(motion.div)`
  background: #f8f9fa;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }

  .photo-info {
    padding: 1rem;
    
    h4 {
      font-size: 1.1rem;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 0.9rem;
      color: #666;
    }
  }
`;

const activities = [
  {
    title: "Behaver Server Development Club",
    period: "2023-2024",
    description: "Participated in server development and backend architecture design studies"
  },
  {
    title: "Dong-A University Startup Club",
    period: "2023-2025",
    description: "Startup idea development and prototype implementation"
  }
];

const photos = [
  {
    id: 0,
    title: "SW Talent Festival Participation",
    date: "2023.08",
    image: `${process.env.PUBLIC_URL}/images/sw_festival.jpg`
  },
  {
    id: 1,
    title: "Bukyung ICT Innovation",
    date: "2024.05",
    image: `${process.env.PUBLIC_URL}/images/busan_award.png`
  },  
  {
    id: 2,
    title: "Eco-friend SW Development Contest(Bloom)",
    date: "2024.10",
    image: `${process.env.PUBLIC_URL}/images/sw_award_bloom.jpg`
  },
  {
    id: 3,
    title: "Dong-A University DevDay SW Contest",
    date: "2024.11",
    image: `${process.env.PUBLIC_URL}/images/dev_day_award.jpg`
  },
  {
    id: 4,
    title: "My Start-up Team",
    date: "2024.01~",
    image: `${process.env.PUBLIC_URL}/images/team.jpg`
  },
  {
    id: 5,
    title: "Hackathon 2025 Regional Problem-Solving Union Hackathon Competition Team",
    date: "2025.02.17 - 2025.02.18",
    image: `${process.env.PUBLIC_URL}/images/hackathon.jpg`
  },
  { 
    id: 6,
    title: "Busan Public Data Startup Competition",
    date: "2025.05.15 - 2025.07.11",
    image: `${process.env.PUBLIC_URL}/images/busan_data_startup.png`,
  },
  {
    id: 7,
    title: "Shenzhen University International Hackathon",
    date: "2025.07.19-2025.07.27",
    image: `${process.env.PUBLIC_URL}/images/szu_hack.png`,
  },
];

const Activities = () => {
  const [showPhotos, setShowPhotos] = useState(false);

  return (
    <ActivitiesSection id="activities">
      <Container>
        <Title>Activities</Title>
        <ActivityList>
          {activities.map((activity, index) => (
            <ActivityItem
              key={index}
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <h3>{activity.title}</h3>
              <p>{activity.period}</p>
              <p>{activity.description}</p>
            </ActivityItem>
          ))}
        </ActivityList>

        <PhotoGallerySection>
          <ToggleButton onClick={() => setShowPhotos(!showPhotos)}>
            {showPhotos ? 'Hide Photos' : 'View Activity Photos'}
          </ToggleButton>

          <AnimatePresence>
            {showPhotos && (
              <PhotoGrid
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5 }}
              >
                {photos.map((photo) => (
                  <PhotoCard
                    key={photo.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <img src={photo.image} alt={photo.title} />
                    <div className="photo-info">
                      <h4>{photo.title}</h4>
                      <p>{photo.date}</p>
                    </div>
                  </PhotoCard>
                ))}
              </PhotoGrid>
            )}
          </AnimatePresence>
        </PhotoGallerySection>
      </Container>
    </ActivitiesSection>
  );
};

export default Activities;
